import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axiosInstance from "../../../interceptors";
import { useTheme } from "../../../context/theme";
import { SEED_SLOTS, SEED_JOBS_FOR_UI } from "../seed-data";

const SLOT_STATUSES = {
  AVAILABLE: { label: "Available", badge: "badge-success" },
  BOOKED: { label: "Booked", badge: "badge-brand" },
  COMPLETED: { label: "Completed", badge: "badge-neutral" },
  CANCELLED: { label: "Cancelled", badge: "badge-danger" },
};

const fadeUp = { hidden: { opacity: 1, y: 0 }, visible: { opacity: 1, y: 0 } };

const Scheduling = () => {
  const { isDark } = useTheme();
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [filterJobId, setFilterJobId] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ jobId: "", dates: "", startTime: "09:00", endTime: "17:00" });
  const [creating, setCreating] = useState(false);
  const [bookingSlot, setBookingSlot] = useState(null);
  const [candidateSearch, setCandidateSearch] = useState("");
  const [candidateResults, setCandidateResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const searchTimer = useRef(null);

  useEffect(() => {
    document.title = "Scheduling - Ample Hire";
    loadSlots();
    loadJobs();
  }, []);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const loadJobs = async () => {
    setJobs(SEED_JOBS_FOR_UI);
  };

  const loadSlots = async (jobId) => {
    setLoading(true);
    try {
      const stored = localStorage.getItem("ample_slots");
      let allSlots = stored ? JSON.parse(stored) : SEED_SLOTS;
      if (!stored) localStorage.setItem("ample_slots", JSON.stringify(SEED_SLOTS));
      const jid = jobId !== undefined ? jobId : filterJobId;
      if (jid) allSlots = allSlots.filter(s => String(s.jobId) === String(jid));
      setSlots(allSlots);
    } catch (e) {
      console.error("Failed to load slots:", e);
      setSlots(SEED_SLOTS);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterJob = (jobId) => {
    setFilterJobId(jobId);
    loadSlots(jobId);
  };

  const createSlots = async () => {
    if (!createForm.jobId || !createForm.dates || !createForm.startTime || !createForm.endTime) return;
    setCreating(true);
    try {
      const dates = createForm.dates.split(",").map((d) => d.trim()).filter(Boolean);
      const job = jobs.find(j => String(j.id) === String(createForm.jobId));
      const newSlots = dates.map(d => ({
        id: "sl" + Date.now() + Math.random().toString(36).slice(2,6),
        date: d, startTime: createForm.startTime, endTime: createForm.endTime,
        status: "AVAILABLE", candidateName: null, candidateEmail: null,
        jobTitle: job?.title || "", jobId: createForm.jobId,
      }));
      const stored = JSON.parse(localStorage.getItem("ample_slots") || "[]");
      const updated = [...stored, ...newSlots];
      localStorage.setItem("ample_slots", JSON.stringify(updated));
      showToast(`${dates.length} slot(s) created`);
      setShowCreate(false);
      setCreateForm({ jobId: "", dates: "", startTime: "09:00", endTime: "17:00" });
      loadSlots();
    } catch (e) {
      showToast("Failed to create slots: " + (e.response?.data?.message || e.message));
    } finally {
      setCreating(false);
    }
  };

  const searchCandidates = (q) => {
    setCandidateSearch(q);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (!q.trim()) { setCandidateResults([]); return; }
    searchTimer.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const r = await axiosInstance.get("/common/user/get-all");
        const all = r.data?.list || (Array.isArray(r.data) ? r.data : []);
        const ql = q.toLowerCase();
        const matched = all
          .filter(u => u.id >= 200)
          .filter(u => `${u.firstName} ${u.lastName}`.toLowerCase().includes(ql) || (u.primaryEmailId||"").toLowerCase().includes(ql))
          .slice(0, 10)
          .map(u => ({ id: u.id, candidateName: `${u.firstName} ${u.lastName}`, candidateEmail: u.primaryEmailId, currentRole: "Candidate" }));
        setCandidateResults(matched);
      } catch {
        setCandidateResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
  };

  const bookSlot = async (slotId, candidateId) => {
    try {
      const candidate = candidateResults.find(c => String(c.id) === String(candidateId));
      const stored = JSON.parse(localStorage.getItem("ample_slots") || "[]");
      const updated = stored.map(s => s.id === slotId ? { ...s, status: "BOOKED", candidateName: candidate?.candidateName || "Candidate", candidateEmail: candidate?.candidateEmail || "" } : s);
      localStorage.setItem("ample_slots", JSON.stringify(updated));
      showToast("Slot booked");
      setBookingSlot(null);
      setCandidateSearch("");
      setCandidateResults([]);
      loadSlots();
    } catch (e) {
      showToast("Booking failed: " + e.message);
    }
  };

  const cancelSlot = async (slotId) => {
    if (!window.confirm("Cancel this booking?")) return;
    try {
      const stored = JSON.parse(localStorage.getItem("ample_slots") || "[]");
      const updated = stored.map(s => s.id === slotId ? { ...s, status: "CANCELLED" } : s);
      localStorage.setItem("ample_slots", JSON.stringify(updated));
      showToast("Booking cancelled");
      loadSlots();
    } catch (e) {
      showToast("Cancel failed: " + e.message);
    }
  };

  // Group slots by date
  const grouped = {};
  slots.forEach((s) => {
    const dateKey = s.date || (s.startTime ? s.startTime.split("T")[0] : "Unknown");
    if (!grouped[dateKey]) grouped[dateKey] = [];
    grouped[dateKey].push(s);
  });
  const sortedDates = Object.keys(grouped).sort();

  return (
    <motion.div
      className="max-w-6xl mx-auto space-y-6"
      initial="hidden"
      animate="visible"
      variants={{ visible: { transition: { staggerChildren: 0.08 } } }}
    >
      <motion.div variants={fadeUp} className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-vf-base font-heading">Scheduling</h1>
          <p className="text-sm mt-1 text-vf-muted">
            Create and manage interview time slots. Book candidates into available slots.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => loadSlots()} className="btn-ghost btn-sm">Refresh</button>
          <button onClick={() => setShowCreate(!showCreate)} className="btn-primary btn-sm">
            {showCreate ? "Cancel" : "Create Slots"}
          </button>
        </div>
      </motion.div>

      {/* Filter by job */}
      <motion.div variants={fadeUp} className="card px-5 py-3 flex items-center gap-3">
        <label className="text-xs font-medium text-vf-muted">Filter by Job:</label>
        <select
          className="select text-sm"
          value={filterJobId}
          onChange={(e) => handleFilterJob(e.target.value)}
        >
          <option value="">All Jobs</option>
          {jobs.map((j) => (
            <option key={j.id} value={j.id}>{j.title || j.jobTitle || j.id}</option>
          ))}
        </select>
        <span className="text-xs text-vf-muted ml-auto">{slots.length} slot(s)</span>
      </motion.div>

      {/* Create Slots Form */}
      <AnimatePresence>
        {showCreate && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="card p-5 space-y-4"
          >
            <h2 className="text-sm font-semibold text-vf-base font-heading">Create Time Slots</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="input-label">Job</label>
                <select
                  className="select w-full"
                  value={createForm.jobId}
                  onChange={(e) => setCreateForm({ ...createForm, jobId: e.target.value })}
                >
                  <option value="">Select a job...</option>
                  {jobs.map((j) => (
                    <option key={j.id} value={j.id}>{j.title || j.jobTitle || j.id}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="input-label">Dates (comma-separated)</label>
                <input
                  className="input w-full"
                  placeholder="e.g. 2026-04-15, 2026-04-16, 2026-04-17"
                  value={createForm.dates}
                  onChange={(e) => setCreateForm({ ...createForm, dates: e.target.value })}
                />
              </div>
              <div>
                <label className="input-label">Start Time</label>
                <input
                  type="time"
                  className="input w-full"
                  value={createForm.startTime}
                  onChange={(e) => setCreateForm({ ...createForm, startTime: e.target.value })}
                />
              </div>
              <div>
                <label className="input-label">End Time</label>
                <input
                  type="time"
                  className="input w-full"
                  value={createForm.endTime}
                  onChange={(e) => setCreateForm({ ...createForm, endTime: e.target.value })}
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2">
              <button onClick={() => setShowCreate(false)} className="btn-ghost btn-sm">Cancel</button>
              <button
                onClick={createSlots}
                disabled={creating || !createForm.jobId || !createForm.dates}
                className="btn-primary btn-sm disabled:opacity-50"
              >
                {creating ? "Creating..." : "Create Slots"}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Slots grouped by date */}
      {loading ? (
        <div className="card p-10 text-center">
          <div className="space-y-3">
            <div className="skeleton h-4 w-48 mx-auto" />
            <div className="skeleton h-4 w-64 mx-auto" />
          </div>
        </div>
      ) : slots.length === 0 ? (
        <motion.div variants={fadeUp} className="card p-10 text-center text-sm text-vf-muted">
          No time slots found. Click "Create Slots" to add interview availability.
        </motion.div>
      ) : (
        sortedDates.map((date) => (
          <motion.div key={date} variants={fadeUp} className="card overflow-hidden">
            <div className="px-5 py-3 border-b border-vf-surface bg-vf-tint">
              <h3 className="text-sm font-semibold text-vf-base font-heading">
                {formatDate(date)}
              </h3>
            </div>
            <div className="divide-y divide-vf-surface">
              {grouped[date].map((slot) => {
                const st = SLOT_STATUSES[slot.status] || { label: slot.status, badge: "badge-neutral" };
                return (
                  <div key={slot.id} className="px-5 py-4 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <div className="text-sm font-medium text-vf-base whitespace-nowrap">
                        {slot.startTime?.split("T")[1]?.substring(0, 5) || slot.startTime || "--:--"}
                        {" - "}
                        {slot.endTime?.split("T")[1]?.substring(0, 5) || slot.endTime || "--:--"}
                      </div>
                      <span className={st.badge}>{st.label}</span>
                      {slot.candidateName && (
                        <div className="text-sm text-vf-muted truncate">
                          {slot.candidateName}
                          {slot.candidateEmail && (
                            <span className="text-xs ml-1 text-vf-muted opacity-70">({slot.candidateEmail})</span>
                          )}
                        </div>
                      )}
                      {slot.jobTitle && (
                        <span className="text-xs text-vf-muted">{slot.jobTitle}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {slot.status === "AVAILABLE" && (
                        <button
                          onClick={() => { setBookingSlot(bookingSlot?.id === slot.id ? null : slot); setCandidateSearch(""); setCandidateResults([]); }}
                          className="btn-secondary btn-sm text-xs"
                        >
                          Book
                        </button>
                      )}
                      {slot.status === "BOOKED" && (
                        <button
                          onClick={() => cancelSlot(slot.id)}
                          className="btn-danger btn-sm text-xs"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Booking inline UI */}
            <AnimatePresence>
              {bookingSlot && grouped[date].some((s) => s.id === bookingSlot.id) && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="px-5 py-4 border-t border-vf-surface bg-vf-tint"
                >
                  <div className="text-xs font-medium text-vf-muted mb-2">Search and select a candidate to book:</div>
                  <div className="relative max-w-md">
                    <input
                      className="input w-full"
                      placeholder="Search by name or email..."
                      value={candidateSearch}
                      onChange={(e) => searchCandidates(e.target.value)}
                      autoFocus
                    />
                    {searchLoading && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <div className="skeleton h-3 w-3 rounded-full" />
                      </div>
                    )}
                  </div>
                  {candidateResults.length > 0 && (
                    <div className="mt-2 max-w-md bg-white rounded-xl border border-vf-surface max-h-48 overflow-y-auto">
                      {candidateResults.map((c) => (
                        <button
                          key={c.id}
                          onClick={() => bookSlot(bookingSlot.id, c.id)}
                          className="w-full text-left px-4 py-2.5 hover:bg-vf-tint transition-colors border-b border-vf-surface last:border-0"
                        >
                          <div className="text-sm font-medium text-vf-base">{c.candidateName || "Unknown"}</div>
                          <div className="text-xs text-vf-muted">{c.candidateEmail || ""} {c.currentRole ? `- ${c.currentRole}` : ""}</div>
                        </button>
                      ))}
                    </div>
                  )}
                  <button onClick={() => setBookingSlot(null)} className="btn-ghost btn-sm text-xs mt-2">Cancel</button>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        ))
      )}

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed bottom-6 right-6 z-[9999] card px-5 py-3 text-sm font-medium text-vf-base shadow-lg"
          >
            {toast}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const formatDate = (dateStr) => {
  try {
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" });
  } catch {
    return dateStr;
  }
};

export default Scheduling;
